/**
 * Studio Navigation Page
 *
 * Renders static navigation pages by URL slug (Data Products nested content).
 */

'use client';

import { publicEnv } from '@/lib/config/public-env';
import { useStudio } from '@/@studio/hooks/useStudio';
import { usePagePermissions } from '@/@auth/hooks/usePagePermissions';
import { useEffectiveOrgUiContext } from '@/@subsidiary/hooks/useEffectiveOrgUiContext';
import { useFeatureFlags } from '@/@feature-flags/hooks/useFeatureFlags';
import { Spinner } from '@heroui/react';
import { useSession } from '@/providers/enhanced-session';
import { useParams, useRouter } from 'next/navigation';
import { useMemo, useEffect } from 'react';

import { CMSComponentRenderer, TitleComponent } from '@/@cms/components';
import { ERROR_MESSAGES } from '@/@cms/types';
import { Card, Media, Title } from '@/@cms/types/entities';
import { createTemplateResolver } from '@/@cms/types/utils';
import { filterCardByPermissions } from '@/@studio/utils';
import { Routes } from '@/app/routes';

/**
 * Studio Navigation Page Component
 *
 * Renders a navigation page based on the URL slug parameter.
 * Each navigation page can contain different sections and cards.
 *
 * @returns JSX.Element - The rendered Navigation Page
 */
export default function StudioNavigationPage() {
    // Get the slug from URL parameters
    const { slug } = useParams();
    const router = useRouter();

    // Fetch navigation data for this specific slug
    const { navigation, isLoadingNavigation, isErrorNavigation, navigationError } = useStudio({
        navigationSlug: slug as string,
    });

    const { data: session } = useSession();
    const effectiveOrg = useEffectiveOrgUiContext();
    const { hasFlag } = useFeatureFlags();

    const { hasAccess } = usePagePermissions(navigation);

    // Redirect to home if user doesn't have access
    useEffect(() => {
        if (!isLoadingNavigation && navigation && !hasAccess) {
            router.replace(Routes.HOME);
        }
    }, [hasAccess, isLoadingNavigation, navigation, router]);

    // Create template resolver with user details and environment variables
    const templateResolver = useMemo(() => {
        const userName = session?.user?.name || 'User';
        const studioUrl = publicEnv('NEXT_PUBLIC_STUDIO_URL') || '';

        return createTemplateResolver({
            'user.name': userName,
            studio: studioUrl,
        });
    }, [session]);

    // Show loading state
    if (isLoadingNavigation) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    // Show error state
    if (isErrorNavigation || !navigation) {
        // Redirect if the error is "No navigation page found for slug"
        if (
            navigationError instanceof Error &&
            navigationError.message.includes(ERROR_MESSAGES.INVALID_SLUG)
        ) {
            router.replace(Routes.HOME);
            return null;
        }
        return (
            <div className="flex h-screen items-center justify-center">
                <p className="text-error-500">
                    Failed to load navigation page
                    {navigationError instanceof Error ? `: ${navigationError.message}` : ''}
                </p>
            </div>
        );
    }

    // Don't render content if user doesn't have access (redirect will happen)
    if (!hasAccess) {
        return null;
    }

    // Additional validation for navigation data
    if (!navigation.Sections || !Array.isArray(navigation.Sections)) {
        return (
            <div className="flex h-screen items-center justify-center">
                <p className="text-error-500">
                    Invalid navigation page structure: Missing sections
                </p>
            </div>
        );
    }

    // Prepare header component if it's a title component
    const headerTitle = navigation.Header?.[0];
    const hasValidHeader =
        headerTitle && headerTitle.__component === 'home.title' && headerTitle.Title;

    // Create a proper Title object if we have a valid header
    const titleComponent: Title | null = hasValidHeader
        ? {
              id: headerTitle.id,
              __component: 'home.title',
              Title: headerTitle.Title,
              Subtitle: headerTitle.Subtitle || null,
              Icon: headerTitle.Icon || '',
              IconAlternate: headerTitle.IconAlternate as unknown as Media | null,
          }
        : null;

    return (
        <div className="container mx-auto space-y-8 py-6">
            {/* Render title component */}
            {titleComponent && (
                <TitleComponent data={titleComponent} resolveTemplate={templateResolver} />
            )}

            {/* Render sections with feature flag checking */}
            <div className="space-y-10">
                {navigation.Sections.map((section) => {
                    // Ensure Cards exists and is an array
                    if (!section.Cards || !Array.isArray(section.Cards)) {
                        return null;
                    }

                    // Filter cards using the shared helper (org name follows View-as;
                    // internal-only tiles hide while Viewing-as)
                    const visibleCards = section.Cards.filter((card) =>
                        filterCardByPermissions(
                            card,
                            effectiveOrg.isInternalForTiles,
                            session?.user?.email || '',
                            effectiveOrg.orgName || '',
                            session?.user?.hasStudioAIAccess,
                            effectiveOrg.hasClientStudioAIAccess,
                            { hasFlag },
                        ),
                    );

                    // Skip rendering section if no visible cards
                    if (visibleCards.length === 0) return null;

                    // Create a modified section with filtered cards
                    const processedSection = {
                        ...section,
                        Cards: visibleCards,
                    };

                    return (
                        <CMSComponentRenderer
                            key={section.id}
                            component={processedSection}
                            additionalProps={{
                                resolveTemplate: templateResolver,
                                onCardClick: (card: Card) =>
                                    console.debug('Navigation card clicked:', card),
                            }}
                        />
                    );
                })}
            </div>

            {/* Fallback for no visible sections */}
            {navigation.Sections.length === 0 && (
                <div className="flex items-center justify-center py-10">
                    <p className="text-foreground/50">No content available for this page</p>
                </div>
            )}
        </div>
    );
}
